import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { matchesText, normalizeText } from "../../utils/searchText";

/**
 * A dropdown you can type into.
 *
 * Typing narrows the list — ignoring case and diacritics, so "pitesti" finds
 * "Pitești" — and the value is always one of the options: text typed and then
 * abandoned reverts rather than becoming a filter on its own. That keeps it a
 * *select* in everything but the typing, which is what the discovery filters
 * need: the API matches a city or a service type by name, and a half-typed
 * "tun" would match nothing.
 *
 * Follows the WAI-ARIA combobox pattern — the input owns focus throughout and
 * points at the highlighted option with `aria-activedescendant` — so the
 * keyboard works as on a native select: arrows move, Enter picks, Escape
 * backs out.
 *
 * Options are plain strings and the string is the value. Pass `options` for a
 * flat list or `groups` for headed sections; `anyLabel` adds a first entry
 * that clears the choice.
 */

// Written out rather than built on `fieldClass`: that constant carries
// `px-3.5`, and appending a `pr-*` for the icons would leave the winner to
// stylesheet order — the shared-class trap in FRONTEND-PATTERNS.md.
const inputClass =
  "w-full truncate rounded-lg border border-ink-200 bg-white py-2.5 pl-3.5 pr-16 text-fluid-sm text-ink-900 placeholder:text-ink-600 transition focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-600/20";

const ComboBox = ({
  id,
  value,
  onChange,
  options,
  groups,
  anyLabel,
  placeholder,
  noMatchesLabel,
  clearLabel,
}) => {
  const [open, setOpen] = useState(false);
  /** What is being typed, or null while the input simply shows the value. */
  const [query, setQuery] = useState(null);
  const [activeIndex, setActiveIndex] = useState(-1);

  const sections = groups || [{ label: null, options: options || [] }];
  const allOptions = sections.flatMap((section) => section.options);

  // A value that arrived in another spelling — a hand-typed link, a city from
  // someone's profile — is shown as the list spells it when the list has it,
  // and as it came when it does not: an honest "Curtea de Arges" beats a blank
  // field that is still filtering.
  const selected =
    allOptions.find((option) => normalizeText(option) === normalizeText(value)) ||
    value;

  const filtering = query !== null && query.trim() !== "";

  const visibleSections = sections
    .map((section) => ({
      ...section,
      options: filtering
        ? section.options.filter((option) => matchesText(option, query))
        : section.options,
    }))
    .filter((section) => section.options.length > 0);

  // One flat list for the keyboard to walk, the "any" entry first. Values are
  // used as given; an empty string is the "any" entry.
  const flat = [
    ...(anyLabel && !filtering ? [""] : []),
    ...visibleSections.flatMap((section) => section.options),
  ];

  const optionId = (index) => `${id}-option-${index}`;

  const close = () => {
    setOpen(false);
    setQuery(null);
    setActiveIndex(-1);
  };

  const openList = () => {
    if (open) return;
    setOpen(true);
    setActiveIndex(Math.max(0, flat.indexOf(selected || "")));
  };

  const choose = (option) => {
    close();
    if (option !== value) onChange(option);
  };

  // Keep the highlighted option in view while the arrows walk past the edge of
  // the scrolled list. jsdom has no scrollIntoView, hence the guard.
  useEffect(() => {
    if (!open || activeIndex < 0) return;
    document
      .getElementById(optionId(activeIndex))
      ?.scrollIntoView?.({ block: "nearest" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, activeIndex]);

  const handleKeyDown = (event) => {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        if (!open) openList();
        else setActiveIndex((index) => Math.min(flat.length - 1, index + 1));
        break;
      case "ArrowUp":
        event.preventDefault();
        if (!open) openList();
        else setActiveIndex((index) => Math.max(0, index - 1));
        break;
      case "Enter":
        // Never submits a surrounding form: Enter here means "this one".
        if (open) {
          event.preventDefault();
          if (activeIndex >= 0 && activeIndex < flat.length) choose(flat[activeIndex]);
        }
        break;
      case "Escape":
        if (open) {
          event.preventDefault();
          close();
        }
        break;
      case "Tab":
        close();
        break;
      default:
        break;
    }
  };

  /** Renders one option; `index` is its place in `flat`. */
  const renderOption = (option, index, label = option) => {
    const isSelected = normalizeText(option) === normalizeText(selected);
    const isActive = index === activeIndex;
    return (
      <li
        key={`${index}-${option}`}
        id={optionId(index)}
        role="option"
        aria-selected={isSelected}
        // mousedown, not click: a click lands after the input's blur, which
        // has already closed the list.
        onMouseDown={(event) => {
          event.preventDefault();
          choose(option);
        }}
        onMouseMove={() => setActiveIndex(index)}
        className={`flex cursor-pointer items-center justify-between gap-3 px-3.5 py-2 text-fluid-sm ${
          isActive ? "bg-brand-50 text-brand-800" : "text-ink-700"
        } ${isSelected ? "font-semibold" : ""}`}
      >
        <span className="truncate">{label}</span>
        {isSelected ? (
          <i className="bi bi-check2 shrink-0 text-brand-600" aria-hidden="true" />
        ) : null}
      </li>
    );
  };

  let nextIndex = anyLabel && !filtering ? 1 : 0;

  return (
    <div className="relative">
      <input
        id={id}
        type="text"
        role="combobox"
        autoComplete="off"
        spellCheck={false}
        aria-autocomplete="list"
        aria-expanded={open}
        aria-controls={`${id}-listbox`}
        aria-activedescendant={
          open && activeIndex >= 0 && activeIndex < flat.length
            ? optionId(activeIndex)
            : undefined
        }
        className={inputClass}
        placeholder={placeholder}
        value={query !== null ? query : selected}
        onChange={(event) => {
          setQuery(event.target.value);
          setOpen(true);
          // The best match, not the "any" entry, is what Enter should pick.
          setActiveIndex(event.target.value.trim() ? 0 : -1);
        }}
        onFocus={(event) => event.target.select()}
        onClick={openList}
        onKeyDown={handleKeyDown}
        onBlur={close}
      />

      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center gap-1 pr-3">
        {value ? (
          <button
            type="button"
            aria-label={clearLabel}
            title={clearLabel}
            // mousedown for the same reason as the options: keep the focus.
            onMouseDown={(event) => {
              event.preventDefault();
              close();
              onChange("");
            }}
            className="pointer-events-auto inline-flex size-6 cursor-pointer items-center justify-center rounded-full text-ink-400 transition hover:bg-ink-100 hover:text-ink-700"
          >
            <i className="bi bi-x-lg text-xs" aria-hidden="true" />
          </button>
        ) : null}
        <i
          className={`bi bi-chevron-down text-xs text-ink-500 transition-transform motion-reduce:transition-none ${
            open ? "rotate-180" : ""
          }`}
          aria-hidden="true"
        />
      </div>

      {open ? (
        <ul
          id={`${id}-listbox`}
          role="listbox"
          className="absolute z-30 mt-1 max-h-72 w-full overflow-auto rounded-lg border border-ink-200 bg-white py-1 shadow-card"
        >
          {anyLabel && !filtering
            ? renderOption("", 0, <span className="text-ink-500">{anyLabel}</span>)
            : null}

          {visibleSections.map((section, sectionIndex) => {
            const headerId = `${id}-group-${sectionIndex}`;
            const items = section.options.map((option) =>
              renderOption(option, nextIndex++),
            );
            return section.label ? (
              <li key={headerId} role="presentation">
                <div
                  id={headerId}
                  className="mt-1 border-t border-ink-100 px-3.5 pb-1 pt-2 text-xs font-semibold uppercase tracking-wide text-ink-400"
                >
                  {section.label}
                </div>
                <ul role="group" aria-labelledby={headerId}>
                  {items}
                </ul>
              </li>
            ) : (
              <React.Fragment key={headerId}>{items}</React.Fragment>
            );
          })}

          {flat.length === 0 ? (
            <li role="presentation" className="px-3.5 py-2 text-fluid-sm text-ink-500">
              {noMatchesLabel}
            </li>
          ) : null}
        </ul>
      ) : null}
    </div>
  );
};

ComboBox.propTypes = {
  /** The input's id, for the `<label htmlFor>` that names it. */
  id: PropTypes.string.isRequired,
  /** The chosen option, or "" for none. */
  value: PropTypes.string,
  /** `(value) => void`, with "" when the choice is cleared. */
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(PropTypes.string),
  groups: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      options: PropTypes.arrayOf(PropTypes.string).isRequired,
    }),
  ),
  /** The first entry, which clears the choice ("Any city"). Omit for none. */
  anyLabel: PropTypes.string,
  /** Shown in the empty input. */
  placeholder: PropTypes.string,
  noMatchesLabel: PropTypes.string,
  /** Accessible name of the × that clears the choice. */
  clearLabel: PropTypes.string,
};

export default ComboBox;
